import { h, Host } from "@stencil/core";
import { limitSlotElements } from "../../../utils/limitSlotElements";
import { logger } from "../../../utils/console";
export class TableRowNeo {
    constructor() {
        this.head = undefined;
        this.span = undefined;
        this.sortkey = undefined;
        this.sortable = undefined;
        this.resizable = undefined;
        this.sorted = undefined;
        this.index = undefined;
        this.loading = undefined;
        this.cellWidth = undefined;
        this.direction = "none";
    }
    host;
    componentDidLoad() {
        limitSlotElements(this.host, {
            allowed: {
                default: ["ux-table-row-neo"]
            }
        });
        this.handleSpanChange();
        this.cellWidth = this.host.clientWidth;
        this.handleResize();
    }
    handleResize() {
        const resizeElement = this.host.shadowRoot?.querySelector("#resize");
        if (resizeElement) {
            let startX;
            let startWidth;
            const minWidth = getComputedStyle(this.host).getPropertyValue("--cell-min-width");
            const maxWidth = getComputedStyle(this.host).getPropertyValue("--cell-max-width");
            const onMouseMove = (event) => {
                const newWidth = startWidth + (event.clientX - startX);
                if (newWidth < parseInt(minWidth, 10) || newWidth > parseInt(maxWidth, 10)) {
                    return;
                }
                this.widthChange.emit({ colIndex: this.index, width: newWidth });
            };
            const onMouseUp = () => {
                resizeElement.classList.remove("onresize");
                document.removeEventListener("mousemove", onMouseMove);
                document.removeEventListener("mouseup", onMouseUp);
            };
            resizeElement.addEventListener("mousedown", (event) => {
                const mouseEvent = event;
                startX = mouseEvent.clientX;
                startWidth = this.host.clientWidth;
                document.addEventListener("mousemove", onMouseMove);
                document.addEventListener("mouseup", onMouseUp);
                resizeElement.classList.add("onresize");
            });
            resizeElement.addEventListener("keydown", (event) => {
                const keyboardEvent = event;
                if (keyboardEvent.key === "ArrowLeft" || keyboardEvent.key === "ArrowRight") {
                    const step = keyboardEvent.key === "ArrowLeft" ? -10 : 10;
                    const newWidth = this.host.clientWidth + step;
                    if (newWidth >= parseInt(minWidth, 10) && newWidth <= parseInt(maxWidth, 10)) {
                        this.widthChange.emit({ colIndex: this.index, width: newWidth });
                    }
                }
            });
        }
    }
    /**
     * UXSort Event - Emits when the cell is sorted
     */
    uxSort;
    /**
     * Handle span change and emit cell changed event
     */
    handleSpanChange() {
        if (this.span > 0) {
            this.host.style.setProperty("--ux-cell-span", this.span.toString());
        }
    }
    handleSortChange(event) {
        if (!this.sortable) {
            return;
        }
        if (!this.sortkey) {
            logger.warn("No sort key provided for table cell");
            return;
        }
        if (event instanceof KeyboardEvent) {
            if (event.key !== "Enter") {
                return;
            }
        }
        this.direction = this.direction === "descending" ? "ascending" : this.direction === "ascending" ? "none" : "descending";
        this.uxSort.emit({ dir: this.direction, key: this.sortkey });
        this.host.setAttribute("aria-sort", this.direction);
        if (this.direction === "none") {
            this.host.removeAttribute("sorted");
            return;
        }
        this.host.setAttribute("sorted", this.direction);
    }
    /**
     * WidthChange Event - Emits when the width of the cell is changed
     */
    widthChange;
    render() {
        return (h(Host, { key: 'ebaaee9bf6c04e22e9b5655f5950e5274306da1a', role: this.head ? "columnheader" : "cell", loading: this.loading }, this.loading && h("ux-skeleton", { key: 'bbdeb78af9012134092b8beb8dc825fbf2b4c010' }), this.head && !this.loading && (h("div", { key: '1247d975161a78532d24d4faed10ab97427b8467', id: "labelContainer", onKeyDown: (e) => this.handleSortChange(e), tabIndex: 0 }, h("ux-copy", { key: '207091d38ffb8113abae31fec981efe9f061413a', variant: "tiny", strong: true }, h("slot", { key: 'dc359fd3d8a9299b9c86317e471cf877ceffeb16' })), this.sortable && (h("ux-table-cell-sort-neo", { key: '094eae2d30f4243f4347eb890cab620a135903b7', onClick: (e) => this.handleSortChange(e), direction: this.direction })), this.resizable && h("button", { key: 'f1f1906d624b7a3a974e65994ead93d5d0f8763a', role: "separator", "aria-orientation": "horizontal", id: "resize" }))), !this.head && !this.loading && h("slot", { key: '462e87ac6a786cb40c959aae0c7e3b3eff08f548' })));
    }
    static get is() { return "ux-table-cell-neo"; }
    static get encapsulation() { return "shadow"; }
    static get originalStyleUrls() {
        return {
            "$": ["table-cell-neo.scss"]
        };
    }
    static get styleUrls() {
        return {
            "$": ["table-cell-neo.css"]
        };
    }
    static get properties() {
        return {
            "head": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "If true, the cell will be rendered as a header cell."
                },
                "attribute": "head",
                "reflect": false
            },
            "span": {
                "type": "number",
                "mutable": false,
                "complexType": {
                    "original": "number",
                    "resolved": "number",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "The number of columns this cell should span."
                },
                "attribute": "span",
                "reflect": false
            },
            "sortkey": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "The key to sort by"
                },
                "attribute": "sortkey",
                "reflect": false
            },
            "sortable": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "If true, the cell will be sortable."
                },
                "attribute": "sortable",
                "reflect": false
            },
            "resizable": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "If true, the cell will be resizable."
                },
                "attribute": "resizable",
                "reflect": false
            },
            "sorted": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "SortDirection",
                    "resolved": "\"ascending\" | \"descending\" | \"none\"",
                    "references": {
                        "SortDirection": {
                            "location": "import",
                            "path": "../interfaces/directions",
                            "id": "src/components/table-neo/interfaces/directions.ts::SortDirection"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "The current sort direction"
                },
                "attribute": "sorted",
                "reflect": false
            },
            "index": {
                "type": "number",
                "mutable": false,
                "complexType": {
                    "original": "number",
                    "resolved": "number",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "The index of the cell"
                },
                "attribute": "index",
                "reflect": false
            },
            "loading": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "If true, the cell will be loading."
                },
                "attribute": "loading",
                "reflect": false
            }
        };
    }
    static get states() {
        return {
            "cellWidth": {},
            "direction": {}
        };
    }
    static get events() {
        return [{
                "method": "uxSort",
                "name": "uxSort",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "UXSort Event - Emits when the cell is sorted"
                },
                "complexType": {
                    "original": "UxSort",
                    "resolved": "UxSort",
                    "references": {
                        "UxSort": {
                            "location": "import",
                            "path": "../interfaces/ux-sort",
                            "id": "src/components/table-neo/interfaces/ux-sort.ts::UxSort"
                        }
                    }
                }
            }, {
                "method": "widthChange",
                "name": "widthChange",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "WidthChange Event - Emits when the width of the cell is changed"
                },
                "complexType": {
                    "original": "WidthChangeEvent",
                    "resolved": "WidthChangeEvent",
                    "references": {
                        "WidthChangeEvent": {
                            "location": "import",
                            "path": "../interfaces/width-change",
                            "id": "src/components/table-neo/interfaces/width-change.ts::WidthChangeEvent"
                        }
                    }
                }
            }];
    }
    static get elementRef() { return "host"; }
    static get watchers() {
        return [{
                "propName": "span",
                "methodName": "handleSpanChange"
            }];
    }
}
//# sourceMappingURL=table-cell-neo.js.map
